import { useState, useEffect } from "react";
import type { modelStateBase, modelState, ModelStateList } from "./modelState";
import { modelStateListSubscriptionManager } from "./modelState";

export function useModelState<T>(modelState: modelState<T>): [T, (value: T) => void] {
    const [state, setState] = useState(modelState.value);

    useEffect(() => {
        const callback = () => setState(modelState.value);
        modelState.subscribe(callback);

        if (modelState.value !== state) {
            setState(modelState.value);
        }

        return () => modelState.unsubscribe(callback);
    }, [state, modelState]);

    return [state, modelState.setValue] as const;
}

export function useModelStateList<T>(modelStateList: ModelStateList<T>): T[] {
    const [state, setState] = useState(modelStateList.toArray());

    useEffect(() => {
        const callback = () => setState(modelStateList.toArray());
        modelStateList.subscribe(callback);
        return () => modelStateList.unsubscribe(callback);
    }, []);

    return state;
}

export function useBulkState<T>(
    list: ModelStateList<T>,
    accessors: ((item: T) => modelStateBase)[]) {

    const [state, setState] = useState(false);

    useEffect(() => {
        const unsubscribeList = list.subscribe(() => {
            setState(!state);
        });

        const unsubscribeManager = modelStateListSubscriptionManager(list, item => {
            const subscriptions = accessors.map(accessor => accessor(item).subscribe(() => {
                setState(!state)
            }));

            return () => {
                subscriptions.forEach(unsubscribe => unsubscribe());
            };
        });

        return () => {
            unsubscribeList();
            unsubscribeManager();
        };
    }, [state]);
}

export function useModelStateConditional<T>(
    modelState: modelState<T>,
    condition: () => boolean,
    deps?: React.DependencyList): [T, (value: T) => void] {
    const [state, setState] = useState(modelState.value);

    useEffect(() => {
        const callback = () => {
            if (condition()) {
                setState(modelState.value)
            }
        };

        modelState.subscribe(callback);
        return () => modelState.unsubscribe(callback);
    }, [modelState, ...deps]);

    return [state, modelState.setValue] as const;
}